import React, { useState, useMemo } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "../../lib/utils";

// Searchable, type-ahead select built on Radix Popover. Portals to <body> at a
// high z-index so the option list always layers above maps / dialogs.
export function Combobox({
  value, onChange, options, placeholder = "Select…", searchPlaceholder = "Search…",
  emptyText = "No match", className, triggerTestId, contentTestId, disabled,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return options;
    return options.filter((o) => String(o.label || o.value).toLowerCase().includes(s));
  }, [q, options]);
  const selected = options.find((o) => o.value === value);

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
      <PopoverPrimitive.Trigger asChild disabled={disabled}>
        <button type="button" data-testid={triggerTestId}
          className={cn(
            "flex h-10 items-center justify-between rounded-sm border border-input bg-background px-3 py-2 text-sm font-mono",
            "focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}>
          <span className={cn("truncate", !selected && "text-muted-foreground")}>{selected ? selected.label : placeholder}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content align="start" sideOffset={4} data-testid={contentTestId}
          className="z-[1300] w-[var(--radix-popover-trigger-width)] min-w-[12rem] overflow-hidden rounded-sm border border-border bg-popover text-popover-foreground shadow-xl">
          <div className="flex items-center border-b border-border px-2">
            <Search className="h-3.5 w-3.5 opacity-50 shrink-0" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={searchPlaceholder}
              className="h-9 w-full bg-transparent px-2 text-sm outline-none" data-testid="combobox-search" />
          </div>
          <div className="max-h-60 overflow-y-auto p-1">
            {filtered.length === 0 && <div className="py-4 text-center text-xs text-muted-foreground">{emptyText}</div>}
            {filtered.map((o) => (
              <button key={o.value} type="button"
                onClick={() => { onChange(o.value); setOpen(false); setQ(""); }}
                className={cn(
                  "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-left outline-none hover:bg-surface focus:bg-surface",
                  value === o.value && "bg-surface"
                )}
                data-testid={`combobox-option-${o.value}`}>
                <Check className={cn("h-3.5 w-3.5 shrink-0", value === o.value ? "opacity-100 text-accent" : "opacity-0")} />
                <span className="truncate">{o.label}</span>
              </button>
            ))}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
